<template>
  <div class="privacy">
    <div class="card">
      <h1 class="page-title">Privacy Policy</h1>
      <p class="last-updated">Last Updated: {{ new Date().toLocaleDateString() }}</p>
      
      <div class="policy-content">
        <section class="policy-section">
          <h2>Introduction</h2>
          <p>Welcome to ImageOptimizer. We respect your privacy and are committed to protecting your personal data. This privacy policy will inform you about how we look after your personal data when you visit our website and tell you about your privacy rights and how the law protects you.</p>
        </section>
        
        <section class="policy-section">
          <h2>Information We Collect</h2>
          <p>When you use ImageOptimizer, we collect the following types of information:</p>
          <ul>
            <li><strong>Images</strong> you upload for compression</li>
            <li><strong>Usage data</strong> such as the frequency and duration of your use of the service</li>
            <li><strong>Technical data</strong> including browser type, IP address, and device information</li>
            <li><strong>Cookies</strong> that help us provide you with a better user experience</li>
          </ul>
        </section>
        
        <section class="policy-section">
          <h2>How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide and maintain the ImageOptimizer service</li>
            <li>Process and compress the images you upload</li>
            <li>Improve our service based on usage patterns</li>
            <li>Protect against fraudulent or unauthorized usage</li>
            <li>Communicate with you about service updates or changes</li>
          </ul>
        </section>
        
        <section class="policy-section">
          <h2>Data Security and Retention</h2>
          <p>We take the security of your data seriously:</p>
          <ul>
            <li>Your uploaded images are processed on secure servers</li>
            <li>Images are automatically deleted shortly after processing (within 24 hours)</li>
            <li>We do not claim any ownership or rights to your images or content</li>
            <li>We implement appropriate security measures to protect against unauthorized access</li>
          </ul>
          <p>We retain your usage data for as long as necessary to fulfill the purposes we collected it for, including for the purposes of satisfying any legal requirements.</p>
        </section>
        
        <section class="policy-section">
          <h2>Cookies and Tracking</h2>
          <p>ImageOptimizer uses cookies to enhance your experience on our site. These cookies help us understand user behavior and improve our service. You can set your browser to refuse all or some browser cookies, but this may prevent some parts of our website from functioning properly.</p>
        </section>
        
        <section class="policy-section">
          <h2>Third-Party Services</h2>
          <p>We may use third-party services to help us operate our website and provide our services. These services may collect information sent by your browser as part of a web page request. They have their own privacy policies that govern how they use this information.</p>
          <p>These third-party services include:</p>
          <ul>
            <li>Analytics providers (to help us understand how users interact with our website)</li>
            <li>Cloud hosting providers (to store and process images)</li>
          </ul>
        </section>
        
        <section class="policy-section">
          <h2>Your Rights</h2>
          <p>Depending on your location, you may have certain rights regarding your personal data, including:</p>
          <ul>
            <li>The right to access information we hold about you</li>
            <li>The right to request correction of your personal data</li>
            <li>The right to request deletion of your personal data</li>
            <li>The right to withdraw consent where we rely on consent to process your data</li>
            <li>The right to object to processing of your personal data</li>
            <li>The right to data portability</li>
          </ul>
          <p>To exercise these rights, please contact us using the information provided in the "Contact Us" section.</p>
        </section>
        
        <section class="policy-section">
          <h2>Children's Privacy</h2>
          <p>Our service is not intended for children under 13 years of age, and we do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us so we can take appropriate action.</p>
        </section>
        
        <section class="policy-section">
          <h2>Changes to This Privacy Policy</h2>
          <p>We may update our privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page and updating the "Last Updated" date at the top of this policy.</p>
          <p>We encourage you to review this privacy policy periodically for any changes. Changes to this privacy policy are effective when they are posted on this page.</p>
        </section>
        
        <section class="policy-section">
          <h2>Contact Us</h2>
          <p>If you have any questions about this privacy policy, please contact us:</p>
          <div class="contact-info">
            <p>Email: privacy@imageoptimizer.com</p>
            <p>Address: 123 Compression Street, Web City, 10101</p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Privacy'
}
</script>

<style scoped>
.privacy {
  max-width: 800px;
  margin: 0 auto;
}

.page-title {
  color: var(--dark-color);
  font-size: 2.2rem;
  margin-bottom: 0.5rem;
  text-align: center;
  position: relative;
  padding-bottom: 1rem;
}

.page-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 4px;
  background: linear-gradient(to right, var(--primary-color), var(--accent-color));
  border-radius: 2px;
}

.last-updated {
  text-align: center;
  color: var(--light-text);
  font-size: 0.9rem;
  margin-bottom: 2rem;
}

.policy-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.policy-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.policy-section h2 {
  color: var(--dark-color);
  font-size: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 0.5rem;
}

.policy-section p {
  font-size: 1rem;
  line-height: 1.7;
  color: var(--text-color);
}

.policy-section ul {
  padding-left: 1.5rem;
  margin-top: 0.5rem;
}

.policy-section li {
  margin-bottom: 0.5rem;
  font-size: 1rem;
  line-height: 1.7;
}

.contact-info {
  background-color: var(--secondary-color);
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
}

.contact-info p {
  margin: 0.5rem 0;
}

@media (max-width: 768px) {
  .page-title {
    font-size: 1.8rem;
  }
  
  .policy-section h2 {
    font-size: 1.3rem;
  }
  
  .policy-section p,
  .policy-section li {
    font-size: 0.95rem;
  }
}
</style> 